import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createEditorUser, deleteUser, fetchUsers, getToken } from './api';
import { input, label } from './adminUi';

export default function UsersDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setErr('');
    try {
      const data = await fetchUsers();
      setUsers(data.users || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    load();
  }, [navigate]);

  async function handleCreate(e) {
    e.preventDefault();
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      await createEditorUser(mobile, password);
      setMobile('');
      setPassword('');
      setMsg('Editor account created. They can sign in with this mobile number and password.');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(phone) {
    if (!window.confirm(`Remove user ${phone}?`)) return;
    setMsg('');
    setErr('');
    try {
      await deleteUser(phone);
      setMsg('User removed.');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 md:p-6">
      <div className="w-full">
        <h2 className="text-lg font-bold text-neutral-900">Team &amp; editors</h2>
        <p className="mt-1 text-sm text-neutral-600">
          Create accounts that can manage <strong>news</strong>, <strong>products</strong>, and{' '}
          <strong>gallery</strong> (and uploads). Only full admins see this page.
        </p>

        {loading ? (
          <p className="mt-6 text-sm text-neutral-500">Loading…</p>
        ) : (
          <>
            <form onSubmit={handleCreate} className="mt-6 space-y-4 rounded-xl border border-neutral-200 bg-white p-4">
              <p className="text-sm font-medium text-neutral-800">Add content editor</p>
              <div>
                <label className={label} htmlFor="new-editor-mobile">
                  Mobile
                </label>
                <input
                  id="new-editor-mobile"
                  className={input}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  autoComplete="tel"
                  placeholder="+880…"
                />
              </div>
              <div>
                <label className={label} htmlFor="new-editor-password">
                  Password
                </label>
                <input
                  id="new-editor-password"
                  type="password"
                  className={input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-brand-olive px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create editor'}
              </button>
            </form>

            <div className="mt-8">
              <p className="text-sm font-medium text-neutral-800">Accounts</p>
              <ul className="mt-2 divide-y divide-neutral-100 rounded-xl border border-neutral-200 bg-white">
                {users.length === 0 ? (
                  <li className="px-4 py-6 text-sm text-neutral-500">No users yet.</li>
                ) : (
                  users.map((u) => (
                    <li
                      key={u.phone}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
                    >
                      <div>
                        <span className="font-medium text-neutral-900">{u.phone}</span>
                        <span
                          className={
                            u.role === 'editor'
                              ? 'ml-2 rounded-full bg-lime-100 px-2 py-0.5 text-xs text-lime-900'
                              : 'ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700'
                          }
                        >
                          {u.role}
                        </span>
                      </div>
                      {u.role === 'editor' ? (
                        <button
                          type="button"
                          onClick={() => handleDelete(u.phone)}
                          className="text-xs font-medium text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </div>
          </>
        )}

        {msg ? <p className="mt-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mt-4 text-sm text-red-600">{err}</p> : null}
      </div>
    </div>
  );
}
